import type { StoreApi } from 'zustand';
import type { MockupJob } from '@eip/engine';
import type { AppState } from './store';
import { nextGeneration } from './runner';
import {
  dropPreviewMaps, dropSceneCovers, getMockupBlank, getPreviewMaps, getSceneCover, mockupBlankKey, mockupPreviewKey,
  putMockupBlank, putMockupPreview, putPreviewMaps, putSceneCover, type MockupSlot,
} from './bitmaps';
import { sceneCoverKey } from '../mockup/gl-job';
import type { TemplateSource } from '../lib/templates';
import type { DocMockup, SceneStatus, TemplateStatus } from './mockup';
import { isStale, MOCKUP_PREVIEW_MAX, type EngineApi } from '../worker/protocol';

export interface MockupRunnerDeps {
  api: Pick<EngineApi, 'mockupLoadTemplate' | 'mockupUnload' | 'mockupRender' | 'mockupBlank' | 'mockupPreviewMaps' | 'mockupLoadScene' | 'mockupUnloadScene' | 'mockupSceneCover'>;
  /** The template's photo as bytes: a built-in is fetched from `/templates`, a
   *  user template read out of its IndexedDB Blob. Injected rather than done
   *  here so the runner stays transport-agnostic (session.ts also marks the
   *  buffer transferable for Comlink). */
  fetchPhoto(source: TemplateSource): Promise<Uint8Array>;
  debounceMs?: number;
}

/**
 * Short compared with the DTF runner's 250 ms: a mockup render is ~25 ms of
 * worker time at preview size, and the controls that drive it (placement
 * sliders, a drag on the canvas) want to feel live. Long enough to swallow the
 * burst of store writes a single drag emits.
 */
export const MOCKUP_DEBOUNCE_MS = 60;

/**
 * Whether cached preview maps have to be fetched again for the preparation the worker is holding
 * now. Pure, and the only place the rule lives, so the runner and its test agree on it.
 *
 * Nothing cached is obviously stale. A preparation we cannot see (`prepared` absent, which is what a
 * template that is not `ready` looks like) is stale too: maps whose preparation cannot be identified
 * could belong to the template the editor just replaced, and drawing from those would put the OLD
 * blank's wrinkles under the art. Otherwise it is one number against one number — `prepGeneration`
 * moves exactly once per (re)preparation, which is exactly when every map changes.
 */
export function previewMapsStale(cached: { prepGeneration: number } | undefined, prepared: { prepGeneration: number } | undefined): boolean {
  if (!cached || !prepared) return true;
  return cached.prepGeneration !== prepared.prepGeneration;
}

/** The scene a job's backdrop names, or null for a backdrop that needs none (a colour, a gradient,
 *  or the photo's own). One reader of the union, so nothing has to know its shape twice. */
export function sceneIdOf(job: MockupJob | undefined): string | null {
  return job?.backdrop?.kind === 'scene' ? job.backdrop.sceneId : null;
}

/**
 * Whether everything a job needs in the WORKER is there: its template's maps, and — for a scene
 * backdrop — that scene's decoded image. Pure, and the only place the rule lives, so the runner and
 * the session agree on it.
 *
 * Reported as one verdict because the slot logic has always had exactly two cases, and a scene fits
 * them both unchanged: not ready means the render is owed (mark the slot rendering, wait for the
 * status to flip), and an `error` means there is a real failure already written on the document.
 * `error` therefore WINS over merely not being ready — a scene that will never load must not leave
 * the slot spinning for a template that is perfectly fine.
 */
export function jobReady(
  job: MockupJob, templateStatus: Record<string, TemplateStatus>, sceneStatus: Record<string, SceneStatus>,
): { ready: boolean; error?: string } {
  const sceneId = sceneIdOf(job);
  const scene = sceneId ? sceneStatus[sceneId] : undefined;
  const template = templateStatus[job.templateId];
  if (template?.state === 'error') return { ready: false, error: template.error ?? `template "${job.templateId}" could not be prepared` };
  if (scene?.state === 'error') return { ready: false, error: scene.error ?? `scene "${sceneId}" could not be loaded` };
  if (template?.state !== 'ready') return { ready: false };
  if (sceneId && scene?.state !== 'ready') return { ready: false };
  return { ready: true };
}

/**
 * Everything that differs between a document's two renders: its own art on its
 * own blank, and — when a front+back pair is bound — the BACK document's art on
 * the style's back blank (`DocMockup.backJob`, derived by the session).
 *
 * The two share the whole of `render`'s generation discipline and differ only in
 * which job they composite, whose output they composite, where the bitmap is
 * cached and which fields of `DocMockup` they keep their bookkeeping in — so
 * they are one code path parameterised here rather than two that could drift.
 */
interface SlotOps {
  job(m: DocMockup): MockupJob | undefined;
  /** The document whose DTF output this render prints — the back slot's is the
   *  bound document, which is the whole point of the second slot. */
  artDocId(m: DocMockup, docId: string): string | undefined;
  /** The generation of the most recently REQUESTED render for this slot. */
  generation(m: DocMockup): number | undefined;
  reserved(generation: number): Partial<DocMockup>;
  landed(generation: number, clipped: boolean, skipped: string[], ms: number, runGeneration: number): Partial<DocMockup>;
  /** Stopped without a render: either nothing to show (no error) or a failure. */
  stopped(error?: string): Partial<DocMockup>;
  /** The slot's current failure, so an error already on the document is not written over itself. */
  error(m: DocMockup): string | undefined;
}
const SLOTS: Record<MockupSlot, SlotOps> = {
  front: {
    job: (m) => m.job,
    artDocId: (_m, docId) => docId,
    generation: (m) => m.renderGeneration,
    reserved: (generation) => ({ rendering: true, renderGeneration: generation, error: undefined }),
    landed: (generation, clipped, skipped, ms, runGeneration) =>
      ({ rendering: false, renderedGeneration: generation, clipped, skipped, ms, runGeneration, error: undefined }),
    stopped: (error) => ({ rendering: false, ...(error === undefined ? {} : { error }) }),
    error: (m) => m.error,
  },
  back: {
    job: (m) => m.backJob,
    artDocId: (m) => m.backDocId ?? undefined,
    generation: (m) => m.backRenderGeneration,
    reserved: (generation) => ({ backRendering: true, backRenderGeneration: generation, backError: undefined }),
    landed: (generation, clipped, skipped, ms, runGeneration) =>
      ({ backRendering: false, backRenderedGeneration: generation, backClipped: clipped, backSkipped: skipped, backRunGeneration: runGeneration, backError: undefined }),
    stopped: (error) => ({ backRendering: false, ...(error === undefined ? {} : { backError: error }) }),
    error: (m) => m.backError,
  },
};

/**
 * Debounced, generation-tokened mockup renderer, mirroring `createSepRunner`:
 * one render per document in flight, a newer request always wins, and a result
 * that lands after a newer one started is dropped — and its bitmap CLOSED,
 * which matters more here than for separations because every dropped render is
 * up to 9 MB of ImageBitmap that nothing else would ever free.
 *
 * Template preparation is separate from rendering and keyed by template rather
 * than document: several documents share one prepared blank, so `ensureTemplate`
 * is idempotent and collapses concurrent callers onto one in-flight promise.
 */
export function createMockupRunner(store: StoreApi<AppState>, deps: MockupRunnerDeps) {
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  /** Preparations in flight, keyed by template id, so N documents entering
   *  mockup mode on the same blank cost one `mockupLoadTemplate`. */
  const loading = new Map<string, Promise<void>>();
  /** The token of the most recent preparation STARTED per template. `unload`
   *  bumps it too, so a preparation whose photo is still in flight when the
   *  template is dropped can never write `ready` for something the worker no
   *  longer holds. */
  const tokens = new Map<string, number>();
  /** Unloads in flight, keyed by template id. `ensureTemplate` waits on one
   *  before it prepares: between `unload` posting to the worker and its status
   *  write landing, the store still described the template it is dropping, so a
   *  preparation started in that window ran alongside the one it was meant to
   *  replace. */
  const unloading = new Map<string, Promise<void>>();
  /** Blank fetches in flight, keyed `${slot cache key}|${template:garment}`, so a
   *  drag that starts, ends and starts again before the first one lands costs one
   *  worker render rather than three. */
  const blanks = new Map<string, Promise<void>>();
  /** Preview-map fetches in flight, keyed by TEMPLATE id (the maps belong to the template, not to a
   *  document or a job), so the viewer asking for the front and the back of a pair — or two
   *  documents on the same blank — costs one `mockupPreviewMaps`. */
  const previewMapFetches = new Map<string, Promise<void>>();
  /** Scene decodes in flight, keyed by scene id, so the front and back of a pair (and every other
   *  document on the same backdrop) cost one `mockupLoadScene` between them. */
  const sceneLoads = new Map<string, Promise<void>>();
  /** The token of the most recent decode STARTED per scene. `unloadScene` bumps it too, so a decode
   *  whose bytes are still in flight can never write `ready` for a scene the worker has dropped. */
  const sceneTokens = new Map<string, number>();
  /** Scene-cover cuts in flight, keyed by `sceneCoverKey`, so the two slots of a pair (and every
   *  other document on the same backdrop and blank) cost one `mockupSceneCover` between them. */
  const sceneCoverFetches = new Map<string, Promise<void>>();
  let disposed = false;

  /** Write a template status only when it actually differs. Every one of these
   *  writes notifies subscribers synchronously, and the session's subscription
   *  calls back into `ensureTemplate` — an unconditional write of a status the
   *  store already holds is how the unknown-template path used to recurse until
   *  the stack blew. */
  function setStatus(id: string, next: TemplateStatus) {
    const cur = store.getState().templateStatus[id];
    if (cur && cur.state === next.state && cur.error === next.error && cur.prepared === next.prepared) return;
    store.getState().setTemplateStatus(id, next);
  }

  /**
   * Record a template as unusable, and release every document waiting on it.
   * Without the second half a failed preparation left those documents
   * `rendering: true` for good, with nothing on screen to say why — the render
   * they were promised is never coming.
   *
   * A back design is a document in its own right (`backDocId` points at one) and
   * carries its own job, so it is covered by the same scan — and so is the back
   * BLANK of a pair, which is nobody's `job.templateId`: a document waiting for
   * one that will not prepare is released through its own `backError`.
   */
  function failTemplate(id: string, message: string) {
    setStatus(id, { state: 'error', prepared: undefined, error: message });
    for (const doc of store.getState().docs) {
      if (!doc.mockup) continue;
      if (doc.mockup.job.templateId === id) {
        if (doc.mockup.rendering || doc.mockup.error !== message) store.getState().setDocMockup(doc.id, { rendering: false, error: message });
      }
      if (doc.mockup.backJob?.templateId === id) {
        if (doc.mockup.backRendering || doc.mockup.backError !== message) store.getState().setDocMockup(doc.id, { backRendering: false, backError: message });
      }
    }
  }

  /**
   * Record a scene as unusable, and release every document waiting on it — the same two halves as
   * `failTemplate`, for the same reason: a document left `rendering: true` for a backdrop that is
   * never coming has nothing on screen to say why.
   *
   * Both slots are scanned. A pair's back tile wears the FRONT job's backdrop (`backJobFor`), so a
   * scene that will not decode stops the back render as surely as the front one.
   */
  function failScene(id: string, message: string) {
    store.getState().setSceneStatus(id, { state: 'error', error: message });
    for (const doc of store.getState().docs) {
      if (!doc.mockup) continue;
      if (sceneIdOf(doc.mockup.job) === id && (doc.mockup.rendering || doc.mockup.error !== message)) {
        store.getState().setDocMockup(doc.id, { rendering: false, error: message });
      }
      if (sceneIdOf(doc.mockup.backJob) === id && (doc.mockup.backRendering || doc.mockup.backError !== message)) {
        store.getState().setDocMockup(doc.id, { backRendering: false, backError: message });
      }
    }
  }

  /** `key` is per document AND slot (`${docId}:${slot}`): the two renders of a
   *  pair are debounced separately, or the back one would swallow the front's. */
  function schedule(key: string, task: () => void) {
    const pending = timers.get(key);
    if (pending) clearTimeout(pending);
    timers.set(key, setTimeout(() => { timers.delete(key); task(); }, deps.debounceMs ?? MOCKUP_DEBOUNCE_MS));
  }

  /**
   * Claim this document's next render generation NOW, before the debounce, and
   * mark it rendering — the same reasoning as the separation runner's `reserve`:
   * everything that decides whether a result may be written reads
   * `renderGeneration`, so a generation allocated only when the render finally
   * runs would leave the whole debounce window looking like nothing newer had
   * been asked for, and an older render resolving inside it would paint the
   * canvas with art the user had already superseded.
   */
  function reserve(id: string, slot: MockupSlot): number {
    const generation = nextGeneration();
    store.getState().setDocMockup(id, SLOTS[slot].reserved(generation));
    return generation;
  }

  /** What a slot needs to render right now, or null when it has nothing to: no
   *  job (no pair bound), or no output to composite. */
  function work(id: string, slot: MockupSlot): { job: MockupJob; artId: string; runGeneration: number; mockup: DocMockup } | null {
    const st = store.getState();
    const doc = st.docs.find((d) => d.id === id);
    if (!doc?.mockup) return null;
    const ops = SLOTS[slot];
    const job = ops.job(doc.mockup);
    const artId = ops.artDocId(doc.mockup, id);
    if (!job || !artId) return null;
    const art = st.docs.find((d) => d.id === artId);
    if (!art?.run) return null;
    return { job, artId, runGeneration: art.run.generation, mockup: doc.mockup };
  }

  async function render(id: string, slot: MockupSlot, generation: number) {
    const ops = SLOTS[slot];
    const w = work(id, slot);
    if (!w) return;
    // Something reserved a newer generation while this one waited out its debounce.
    if (isStale(generation, ops.generation(w.mockup) ?? generation)) return;
    // The template was unloaded (or failed to re-prepare) while we waited — or the job's backdrop
    // scene was: clear the flag, or the panel reads "Rendering…" for good. Nothing is sent to the
    // worker, which would throw 'scene not loaded' and put that on the document instead of the
    // real reason.
    if (!jobReady(w.job, store.getState().templateStatus, store.getState().sceneStatus).ready) { store.getState().setDocMockup(id, ops.stopped()); return; }
    // Captured BEFORE the await: a DTF run landing WHILE this render is in flight
    // must not relabel this mockup with the newer generation — it composited the
    // output that was current when the render STARTED.
    const runGeneration = w.runGeneration;
    try {
      const result = await deps.api.mockupRender(w.artId, w.job, MOCKUP_PREVIEW_MAX, generation);
      if (disposed) { result.bitmap?.close(); return; }
      const current = store.getState().docs.find((d) => d.id === id);
      // Closed, reset, or superseded while the worker was compositing: the
      // bitmap is ours to free, and nobody will ever read it.
      if (!current?.mockup || isStale(generation, ops.generation(current.mockup) ?? generation)) { result.bitmap?.close(); return; }
      if (result.cancelled) {
        // The worker drops a render when a NEWER generation for the same
        // DOCUMENT arrives — which, for a pair, can be the other slot's render
        // of the same document's art rather than a newer version of this one
        // (the back slot prints the bound document, whose own front preview
        // shares that document id in the worker). This generation is still the
        // latest for THIS slot, so nothing else will ever fill it: re-schedule
        // rather than leave the slot blank and "rendering" for good.
        result.bitmap?.close();
        triggerSlot(id, slot);
        return;
      }
      if (!result.bitmap) { store.getState().setDocMockup(id, ops.stopped('the mockup render produced no image')); return; }
      // Cached before the store write, so a subscriber woken by the write
      // (the viewer) finds the bitmap the new generation points at.
      putMockupPreview(mockupPreviewKey(id, slot), generation, result.bitmap, result.width, result.height);
      store.getState().setDocMockup(id, ops.landed(generation, result.clipped, result.skipped, result.ms, runGeneration));
    } catch (err) {
      if (disposed) return;
      const current = store.getState().docs.find((d) => d.id === id);
      if (!current?.mockup) return;
      if (isStale(generation, ops.generation(current.mockup) ?? generation)) return;   // a newer render owns the slot
      store.getState().setDocMockup(id, ops.stopped((err as Error).message));
    }
  }

  /**
   * Re-render one slot of one document, debounced. Shared by `trigger` and by
   * the cancelled-render retry above; both want exactly the same guards.
   */
  function triggerSlot(id: string, slot: MockupSlot) {
    const st = store.getState();
    if (st.appMode !== 'mockup') return;
    const w = work(id, slot);
    if (!w) return;
    // The template's maps AND the backdrop scene's pixels: a job waiting for either is a render
    // that is owed, and the two are indistinguishable from here (see `jobReady`).
    const gate = jobReady(w.job, st.templateStatus, st.sceneStatus);
    if (!gate.ready) {
      // Not renderable yet, but the render is OWED. Reserving the generation
      // anyway (rather than only setting `rendering`) is what makes that owed
      // render representable — `renderGeneration !== renderedGeneration` — so
      // the mode-entry catch-up can find it if the template becomes ready
      // while the user is in another mode and nothing re-triggers. Nothing is
      // scheduled; the session triggers again on the flip to `ready`.
      //
      // Not for an `error`: that document has just been given a real error by `failTemplate` (or
      // `failScene`), and marking it rendering would hide it behind a spinner that never stops.
      if (gate.error === undefined) reserve(id, slot);
      // A gate that has already FAILED still has to reach THIS document, because `failTemplate` and
      // `failScene` only wrote the error onto the documents naming it at the moment of failure: a
      // document switched onto a scene that is already in `error` would otherwise keep showing the
      // previous render under a job it no longer matches, with the reason nowhere on screen.
      // Guarded on the value, exactly as those two are, so an unchanged error is not a store write.
      else if (SLOTS[slot].error(w.mockup) !== gate.error) store.getState().setDocMockup(id, SLOTS[slot].stopped(gate.error));
      return;
    }
    const generation = reserve(id, slot);
    schedule(`${id}:${slot}`, () => void render(id, slot, generation));
  }

  /** A job's blank cache key, resolved through the registry and the open documents
   *  so the viewer — which calls the same `mockupBlankKey` to read the cache —
   *  always agrees with what was stored. `artId` is the slot's own art document:
   *  what an extra print's `docId: null` means, and whose run generation therefore
   *  counts towards the key. */
  function blankKeyFor(job: MockupJob, artId: string): string {
    const st = store.getState();
    const template = st.templates.find((t) => t.template.id === job.templateId)?.template;
    return mockupBlankKey(job, template, st.docs, artId);
  }

  /** `ensureBlank`'s body, once the work is known to be worth doing. */
  function startBlank(slotKey: string, key: string, artId: string, job: MockupJob, id: string, slot: MockupSlot): Promise<void> {
    const inFlight = `${slotKey}|${key}`;
    const pending = blanks.get(inFlight);
    if (pending) return pending;
    // The deferred shape `startTemplate` uses, and for the same reason: the entry
    // has to be in `blanks` before anything can throw, or a synchronous failure
    // would leave a settled promise parked in the map for good.
    let settle!: () => void;
    const promise = new Promise<void>((resolve) => { settle = resolve; });
    blanks.set(inFlight, promise);
    void (async () => {
      try {
        const result = await deps.api.mockupBlank(artId, job, MOCKUP_PREVIEW_MAX);
        // Closed, or moved to another blank or colourway while the worker was
        // compositing: nothing will ever draw this, and it is ours to free.
        const mockup = disposed ? undefined : store.getState().docs.find((d) => d.id === id)?.mockup;
        const live = mockup && SLOTS[slot].job(mockup);
        if (!live || blankKeyFor(live, artId) !== key) result.bitmap.close();
        else putMockupBlank(slotKey, key, result.bitmap, result.width, result.height);
      } catch {
        // A blank is an optimisation and nothing else: without one the drag falls
        // back to the live renders it always used, so a failure is not worth a
        // message — and the next drag asks again.
      } finally {
        if (blanks.get(inFlight) === promise) blanks.delete(inFlight);
        settle();
      }
    })();
    return promise;
  }

  /** `ensurePreviewMaps`'s body, once the fetch is known to be worth making. */
  function startPreviewMaps(id: string): Promise<void> {
    // The deferred shape `startBlank` uses, and for the same reason: the entry has to be in the
    // in-flight map before anything can throw.
    let settle!: () => void;
    const promise = new Promise<void>((resolve) => { settle = resolve; });
    previewMapFetches.set(id, promise);
    void (async () => {
      try {
        const maps = await deps.api.mockupPreviewMaps(id, MOCKUP_PREVIEW_MAX);
        const status = disposed ? undefined : store.getState().templateStatus[id];
        // Unloaded, re-prepared or failed while the worker was measuring: these maps describe a
        // preparation nothing holds any more, and the photo bitmap in them is ours to free.
        // `maps.prepGeneration` is what the worker built them from, so this compares the two
        // preparations directly rather than trusting a generation captured before the await.
        //
        // `unloading.has(id)` is the third of those and not covered by the other two: `unload` drops
        // the cache and bumps the token BEFORE it awaits the worker, and only writes `idle` after —
        // so inside that window the status still says `ready` with the SAME preparation these maps
        // were built from, and re-caching them here would park an entry (tens of MB plus an
        // `ImageBitmap`) that nothing will ever draw from and, for a template being DELETED, nothing
        // will ever replace.
        if (status?.state !== 'ready' || unloading.has(id) || previewMapsStale(maps, status.prepared)) maps.photo.close();
        else putPreviewMaps(maps);
      } catch {
        // The GPU path is an optimisation and nothing else: without maps the viewer shows the CPU
        // render it always did, so a failure is not worth a message — and the next trigger asks again.
      } finally {
        if (previewMapFetches.get(id) === promise) previewMapFetches.delete(id);
        settle();
      }
    })();
    return promise;
  }

  /** `ensureSceneCover`'s body, once the cut is known to be worth making. */
  function startSceneCover(sceneId: string, key: string, width: number, height: number): Promise<void> {
    // The deferred shape `startPreviewMaps` uses, and for the same reason: the entry has to be in
    // the in-flight map before anything can throw.
    let settle!: () => void;
    const promise = new Promise<void>((resolve) => { settle = resolve; });
    sceneCoverFetches.set(key, promise);
    // Captured BEFORE the await, and compared after: `unloadScene` bumps this, so a cover cut from
    // pixels the worker has since dropped (or replaced) cannot be cached over the top of it.
    const token = sceneTokens.get(sceneId) ?? 0;
    void (async () => {
      try {
        const bitmap = await deps.api.mockupSceneCover(sceneId, width, height);
        const status = disposed ? undefined : store.getState().sceneStatus[sceneId];
        if (disposed || status?.state !== 'ready' || (sceneTokens.get(sceneId) ?? 0) !== token) bitmap.close();
        else putSceneCover(key, bitmap);
      } catch {
        // The GPU path is an optimisation and nothing else: without a cover the viewer shows the
        // CPU render, which draws the same backdrop from the worker's own copy of the scene.
      } finally {
        if (sceneCoverFetches.get(key) === promise) sceneCoverFetches.delete(key);
        settle();
      }
    })();
    return promise;
  }

  /** `ensureTemplate`'s body, once nothing is standing in the way of starting. */
  function startTemplate(id: string): Promise<void> {
    const st = store.getState();
    const status = st.templateStatus[id];
    if (status?.state === 'ready') return Promise.resolve();
    // `error` is TERMINAL, re-armed only by `unload` (or by the registry being
    // rebuilt without it). Retrying here would both recurse without bound —
    // the status write notifies, the subscription calls back in — and
    // re-attempt a hopeless preparation on every unrelated store change. The
    // caller is told why instead.
    if (status?.state === 'error') return Promise.reject(new Error(status.error ?? `template "${id}" could not be prepared`));
    const source = st.templates.find((t) => t.template.id === id);
    if (!source) {
      // A document referencing a template that is no longer in the registry (a
      // deleted user template, a redeploy that dropped a blank).
      const message = `template "${id}" is no longer available`;
      failTemplate(id, message);
      return Promise.reject(new Error(message));
    }
    const token = (tokens.get(id) ?? 0) + 1;
    tokens.set(id, token);
    let settle!: { resolve: () => void; reject: (e: Error) => void };
    const promise = new Promise<void>((resolve, reject) => { settle = { resolve, reject }; });
    // Registered BEFORE the status write: that write notifies subscribers
    // synchronously and the session calls straight back in here, which used to
    // find an empty `loading` map and return a resolved promise for a
    // preparation that had not started yet.
    loading.set(id, promise);
    setStatus(id, { state: 'preparing', prepared: undefined, error: undefined });
    void (async () => {
      try {
        const bytes = await deps.fetchPhoto(source);
        const prepared = await deps.api.mockupLoadTemplate(source.template, bytes);
        // Unloaded or superseded while the photo was in flight: the worker no
        // longer holds what this result describes.
        if (disposed || tokens.get(id) !== token) { settle.resolve(); return; }
        setStatus(id, { state: 'ready', prepared, error: undefined });
        settle.resolve();
      } catch (err) {
        const message = (err as Error).message;
        if (!disposed && tokens.get(id) === token) failTemplate(id, message);
        settle.reject(new Error(message));
      } finally {
        // Only ever our own entry: a superseded preparation's token has moved
        // on, and deleting the current one would let `ensureTemplate` start a
        // second preparation alongside the live one.
        if (tokens.get(id) === token) loading.delete(id);
      }
    })();
    return promise;
  }

  /** `ensureScene`'s body, once nothing is standing in the way of starting. */
  function startScene(id: string): Promise<void> {
    const st = store.getState();
    const status = st.sceneStatus[id];
    if (status?.state === 'ready') return Promise.resolve();
    // `error` is TERMINAL, re-armed only by `unloadScene` (or by the scene list being rebuilt
    // without it, which prunes the status). Retrying here would recurse — the status write
    // notifies, the session calls back in — and re-attempt a hopeless decode on every unrelated
    // store change. The caller is told why instead.
    if (status?.state === 'error') return Promise.reject(new Error(status.error ?? `scene "${id}" could not be loaded`));
    const source = st.scenes.find((sc) => sc.id === id);
    if (!source) {
      // A job naming a scene that is no longer in the store (deleted in another tab, or a payload
      // from elsewhere). `sanitizeJob` clears such a backdrop wherever the list is known; this is
      // the path for a job that got here without it.
      const message = 'scene not found';
      failScene(id, message);
      return Promise.reject(new Error(message));
    }
    const token = (sceneTokens.get(id) ?? 0) + 1;
    sceneTokens.set(id, token);
    let settle!: { resolve: () => void; reject: (e: Error) => void };
    const promise = new Promise<void>((resolve, reject) => { settle = { resolve, reject }; });
    // Registered BEFORE the status write, as `startTemplate` is: that write notifies subscribers
    // synchronously and the session calls straight back in here, which would otherwise find an
    // empty map and hand back a resolved promise for a decode that had not started yet.
    sceneLoads.set(id, promise);
    store.getState().setSceneStatus(id, { state: 'loading', error: undefined });
    void (async () => {
      try {
        // MINOR (final review 7, left as is): these bytes are structured-CLONED to the worker, where
        // `addScene` transfers the same ones on import (`transferBytes`) — one extra copy of up to
        // 25 MB, once per scene per session, on the first render after a reload. Closing it means
        // injecting a `readScene` beside `fetchPhoto` so the runner stays transport-agnostic.
        const bytes = new Uint8Array(await source.bytes.arrayBuffer());
        await deps.api.mockupLoadScene(id, bytes);
        // Dropped or superseded while the bytes were in flight: the worker no longer holds what
        // this decode describes.
        if (disposed || sceneTokens.get(id) !== token) { settle.resolve(); return; }
        store.getState().setSceneStatus(id, { state: 'ready', error: undefined });
        settle.resolve();
      } catch (err) {
        const message = (err as Error).message;
        if (!disposed && sceneTokens.get(id) === token) failScene(id, message);
        settle.reject(new Error(message));
      } finally {
        // Only ever our own entry: a superseded decode's token has moved on, and deleting the
        // current one would let `ensureScene` start a second decode beside the live one.
        if (sceneTokens.get(id) === token) sceneLoads.delete(id);
      }
    })();
    return promise;
  }

  return {
    /**
     * Prepare a template in the worker if it is not prepared (or being prepared)
     * already, moving its status `preparing` → `ready` | `error`. Safe to call on
     * every store change: a ready or in-flight template returns immediately, and
     * concurrent callers share the one promise.
     *
     * The promise RESOLVES once the blank is prepared and REJECTS when the
     * preparation failed — the export paths await it before they render, and a
     * promise that resolved on a failure would send them to the worker for a
     * blank it does not hold. (A preparation superseded by `unload` resolves:
     * nothing failed, the template was simply dropped, and the caller's own
     * readiness check is what catches that.)
     */
    ensureTemplate(id: string): Promise<void> {
      const inFlight = loading.get(id);
      if (inFlight) return inFlight;
      const dropping = unloading.get(id);
      if (!dropping) return startTemplate(id);
      // A drop is in flight: what the store says about this template right now
      // describes one the worker is in the middle of forgetting, and a
      // preparation started here would run beside the one `unload` exists to
      // make room for (a re-save from the editor is exactly that sequence).
      // Wait it out, then prepare from the `idle` status it leaves behind.
      // Registered in `loading` FIRST, so callers arriving inside the window
      // share this promise rather than each starting a preparation of their own.
      let settle!: { resolve: () => void; reject: (e: Error) => void };
      const promise = new Promise<void>((resolve, reject) => { settle = { resolve, reject }; });
      loading.set(id, promise);
      void (async () => {
        await dropping;
        // `startTemplate` registers an entry of its own, so ours has to be out
        // of the way first — otherwise it would find this promise and hand it
        // straight back, waiting on itself.
        if (loading.get(id) === promise) loading.delete(id);
        if (disposed) { settle.resolve(); return; }
        try { await startTemplate(id); settle.resolve(); } catch (e) { settle.reject(e as Error); }
      })();
      return promise;
    },

    /**
     * Decode a job's backdrop scene in the worker if it is not there (or on its way) already,
     * moving its status `loading` → `ready` | `error`. Safe to call on every store change: a ready
     * or in-flight scene returns immediately, and concurrent callers — the front and back of a
     * pair, every document on the same backdrop — share the one promise and the one decode.
     *
     * RESOLVES once the scene is in the worker and REJECTS when it could not be read, the same
     * contract `ensureTemplate` gives the export paths. (A decode superseded by `unloadScene`
     * resolves: nothing failed, the scene was simply dropped, and the caller's own readiness check
     * is what catches that.)
     */
    ensureScene(id: string): Promise<void> {
      const inFlight = sceneLoads.get(id);
      if (inFlight) return inFlight;
      return startScene(id);
    },

    /**
     * Drop a scene from the worker (the shop deleting a backdrop) and return its status to `idle`,
     * so a job that names it again decodes it again — and so an `error` status is re-armed.
     *
     * The same ordering as `unload`: the token is bumped FIRST, so a decode still in flight cannot
     * write `ready` for a scene the worker is about to forget, and the status is written LAST,
     * after the worker has actually dropped it, because that write notifies subscribers
     * synchronously and `idle` is exactly the status that invites them to decode it again.
     *
     * No `unloading`-style wait map, unlike `unload`: a scene id is a fresh UUID per import and is
     * never re-saved under the same one, and `removeScene` clears every job's backdrop BEFORE it
     * calls this — so there is nothing left to ask for the scene in the window this opens.
     */
    unloadScene(id: string): Promise<void> {
      sceneTokens.set(id, (sceneTokens.get(id) ?? 0) + 1);
      sceneLoads.delete(id);
      // The cached covers were cut from the pixels the worker is about to forget. Dropped here
      // rather than in the `finally` for the same reason the token is bumped first: nothing may
      // draw from them after this point. A cut still in flight closes its own bitmap — its
      // post-await token check sees the bump above.
      dropSceneCovers(id);
      return (async () => {
        try {
          await deps.api.mockupUnloadScene(id);
        } finally {
          // Even on a failed unload: the worker's copy is then in an unknown state, and
          // `mockupLoadScene` replaces whatever is there, so re-decoding is both safe and the only
          // way back.
          if (!disposed) store.getState().setSceneStatus(id, { state: 'idle', error: undefined });
        }
      })();
    },

    /**
     * Cache the art-free garment for one slot's CURRENT job, so a placement drag
     * can paint a ghost over it instead of asking the worker for a render per
     * frame. Idempotent: a blank already cached for that (template, colour)
     * returns at once, and concurrent callers share the one in-flight fetch.
     *
     * Resolves once the bitmap is cached — or once it is known it will not be
     * (the template is not prepared, the document has no job, the render threw).
     * It never rejects: the caller's next look at `mockupBlanks` is what decides
     * whether there is a ghost to draw, and the live-render drag is the fallback.
     */
    ensureBlank(id: string, slot: MockupSlot = 'front'): Promise<void> {
      const st = store.getState();
      const mockup = st.docs.find((d) => d.id === id)?.mockup;
      if (!mockup) return Promise.resolve();
      const ops = SLOTS[slot];
      const job = ops.job(mockup);
      const artId = ops.artDocId(mockup, id);
      if (!job || !artId) return Promise.resolve();
      // Nothing to composite onto yet — no maps, or no backdrop scene decoded (a blank goes
      // through the very same composite, so it needs both). The drag falls back to live renders,
      // and the next one asks again — by which time the session will have loaded them.
      if (!jobReady(job, st.templateStatus, st.sceneStatus).ready) return Promise.resolve();
      const slotKey = mockupPreviewKey(id, slot);
      const key = blankKeyFor(job, artId);
      if (getMockupBlank(slotKey, key)) return Promise.resolve();
      return startBlank(slotKey, key, artId, job, id, slot);
    },

    /**
     * Cache one TEMPLATE's shading maps and downscaled photo, so the viewer's GPU renderer can draw
     * the whole simulation per animation frame instead of asking the worker for a composite.
     * Idempotent: maps already cached for the preparation the worker holds return at once, and
     * concurrent callers (the front and back of a pair, two documents on one blank) share the one
     * fetch.
     *
     * A no-op unless the template is `ready` — there is nothing to measure otherwise, and the
     * viewer's GL path simply stays off until the preparation lands and the caller asks again.
     *
     * It never rejects. The GPU preview is an optimisation on top of a CPU path that works: what
     * decides whether a frame goes to the GPU is the caller's next look at the cache, not this
     * promise, so a failure just leaves the CPU render on screen.
     */
    ensurePreviewMaps(templateId: string): Promise<void> {
      const status = store.getState().templateStatus[templateId];
      if (status?.state !== 'ready' || !status.prepared) return Promise.resolve();
      if (!previewMapsStale(getPreviewMaps(templateId), status.prepared)) return Promise.resolve();
      const pending = previewMapFetches.get(templateId);
      if (pending) return pending;
      return startPreviewMaps(templateId);
    },

    /**
     * Cache one backdrop SCENE cut to cover `width × height` exactly, so the GPU preview can draw
     * the swap the CPU render is drawing. Idempotent: a cover already cached returns at once, and
     * concurrent callers (the two slots of a pair, two documents on one blank) share the one cut.
     *
     * A no-op unless the scene is `ready` in the worker — there is nothing to cut otherwise, and
     * `mockupSceneCover` would throw 'scene not loaded'. The session's `ensureScene` is what gets it
     * there, and the caller asks again when the status flips.
     *
     * It never rejects, for the reason `ensurePreviewMaps` does not: what decides whether the frame
     * goes to the GPU is the caller's next look at the cache, and a failure leaves the CPU render —
     * which draws the backdrop from the worker's own scene — on screen.
     */
    ensureSceneCover(sceneId: string, width: number, height: number): Promise<void> {
      const key = sceneCoverKey(sceneId, width, height);
      if (getSceneCover(key)) return Promise.resolve();
      const pending = sceneCoverFetches.get(key);
      if (pending) return pending;
      if (store.getState().sceneStatus[sceneId]?.state !== 'ready') return Promise.resolve();
      return startSceneCover(sceneId, key, width, height);
    },

    /**
     * Re-render one document's mockup: its own art on its own blank
     * (`slot: 'front'`, the default), or the back half of a bound pair — the
     * BOUND document's art on the style's back blank (`slot: 'back'`, the
     * session's derived `backJob`). A no-op outside mockup mode, for a slot with
     * no job, or with no output to composite.
     *
     * While the template is still preparing the slot is marked `rendering` and
     * nothing is scheduled: the session re-triggers the moment the status flips
     * to `ready`, so a mode entry does not need to wait for the photo before the
     * panel shows it is working.
     */
    trigger(id: string, slot: MockupSlot = 'front') { triggerSlot(id, slot); },

    /**
     * Drop a prepared template from the worker (a user template being deleted or
     * re-saved) and return its status to `idle`, so the next document that needs
     * it prepares it again — and so an `error` status is re-armed.
     *
     * Order matters twice. The token is bumped FIRST, so a preparation still in
     * flight cannot write `ready` for a template the worker is about to drop.
     * The status is written LAST, after the worker has actually dropped it,
     * because that write notifies subscribers synchronously and `idle` is
     * exactly the status that invites them to prepare it again.
     *
     * The whole of it is published in `unloading` while it runs, and
     * `ensureTemplate` waits on that: the window between the worker being told
     * to drop the template and the status saying so is the one place a second
     * preparation could start beside the one this call is making room for.
     */
    unload(id: string): Promise<void> {
      tokens.set(id, (tokens.get(id) ?? 0) + 1);
      loading.delete(id);
      // The cached preview maps describe the preparation being dropped, down to the photo bitmap
      // they carry. Dropped here rather than in the `finally` for the same reason the token is
      // bumped first: nothing may draw from them after this point. A fetch still in flight closes
      // its own result — its post-await check sees the `idle` status this call leaves behind.
      dropPreviewMaps(id);
      // Published BEFORE the worker is told anything, so there is no window at
      // all in which `ensureTemplate` could miss the drop. It always RESOLVES:
      // a failed unload still re-arms the template (see below), so a waiter
      // proceeds either way — the rejection belongs to this call's own caller.
      let settle!: () => void;
      const waited = new Promise<void>((resolve) => { settle = resolve; });
      unloading.set(id, waited);
      return (async () => {
        try {
          await deps.api.mockupUnload(id);
        } finally {
          // Even on a failed unload: the worker's copy is then in an unknown
          // state, and `mockupLoadTemplate` is idempotent, so re-preparing is
          // both safe and the only way back.
          if (!disposed) setStatus(id, { state: 'idle', prepared: undefined, error: undefined });
          // Cleared before the waiters are released, so the preparation they go
          // on to start is not made to wait for a drop that is already done.
          if (unloading.get(id) === waited) unloading.delete(id);
          settle();
        }
      })();
    },

    dispose() {
      disposed = true;
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      loading.clear();
      tokens.clear();
      unloading.clear();
      blanks.clear();
      previewMapFetches.clear();
      sceneLoads.clear();
      sceneTokens.clear();
      sceneCoverFetches.clear();
    },
  };
}
